import styled from '@emotion/styled'
import Link from 'next/link'

import { usePreviewController } from '../../WidgetPreview/previewController'
import DisconnectChatIntegrationModal from '../DisconnectChatIntegrationModal'
import Badge from 'components/Badge'
import Button from 'components/Button'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import { Portal } from 'components/Portal'
import StatusBanner from 'components/StatusBanner'
import UseAsyncFn from 'components/UseAsyncFn'
import Video from 'components/Video'
import YouTubeVideoModal from 'components/YouTubeVideoModal'
import { ChatsIcon, ClockIcon, EyeIcon } from 'components/icons'
import { useIntegration } from 'hooks/useIntegration'
import { useTextProductState } from 'hooks/useTextProductState'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { ActionButtonsPortalContext } from 'layouts/DefaultLayout'
import { connectIntegration, disconnectIntegration } from 'lib/api'
import { LiveChatIntegrationUrls } from 'lib/constants'
import { validateFetchError } from 'lib/fetcher'
import { isMobile } from 'lib/helpers'
import { store, useAppDispatch } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: ${({ theme }) => theme.spaces.space7};
`

const VideoSection = styled.div`
	background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
	border-radius: ${({ theme }) => theme.borderRadiuses.xxl};
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
`

const Explanation = styled(FeatureExplanation)`
	margin-bottom: 0;
`

const ButtonColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space4};
`

const StyledButton = styled(Button)`
	padding: ${({ theme }) => theme.spaces.space4};
`

const BaseLink = styled(Link)`
	text-decoration: none;
`

const EnabledBadge = styled(Badge)`
	background-color: ${({ theme }) => theme.colors.light.desaturetedGreen};
	display: flex;
	justify-content: center;
	white-space: normal;
`

const ColorResetLink = styled(Link)`
	color: inherit;
`

function LiveChatIntegration() {
	const mobile = isMobile()
	const track = useTracking()
	const showToast = useToast()
	const dispatch = useAppDispatch()
	const previewController = usePreviewController()
	const { integration, mutateIntegration } = useIntegration('livechat')
	const liveChatProductState = useTextProductState('LiveChat')

	if (!integration) {
		return null
	}

	function showDisconnectModal() {
		dispatch({ type: 'currentModal/set', payload: 'ConfirmDisableModal' })
	}

	function hideDisconnectModal() {
		dispatch({ type: 'currentModal/set', payload: null })
	}

	async function handleConnect() {
		try {
			await connectIntegration('livechat', undefined)
			mutateIntegration({ connected: true, isLicenseActive: true }, { revalidate: false })
			previewController.showView('Homescreen', 'contact-card')
			showToast('success', 'LiveChat integration connected')
		} catch (error) {
			if (validateFetchError(error)) {
				if (error.data.message === 'LiveChat license is not active') {
					showToast(
						'error',
						`Your LiveChat trial has expired. [Contact support to renew it →](${LiveChatIntegrationUrls.LIVECHAT_SUPPORT})`,
						15000,
					)
				} else {
					showToast('error', error.data.message)
				}
			} else {
				showToast('error', 'LiveChat integration failed to connect')
			}
		}
	}

	async function handleDisconnect() {
		try {
			await disconnectIntegration('livechat')
			mutateIntegration({ connected: false }, { revalidate: false })
			showToast('success', 'LiveChat integration disconnected')
			hideDisconnectModal()
		} catch (error) {
			showToast('error', validateFetchError(error) ? error.data.message : 'LiveChat integration failed to disconnect')
		}
	}

	const disconnectButton = (
		<StyledButton
			fullWidth
			type="button"
			kind="destructive-secondary"
			onClick={() => {
				showDisconnectModal()
				track(EVENT_NAMES.LIVECHAT_DISCONNECT_CLICKED)
			}}
			disableHover
		>
			Disconnect
		</StyledButton>
	)

	return (
		<Wrapper>
			<YouTubeVideoModal src={LiveChatIntegrationUrls.PROMO_VIDEO_EMBED} />
			{integration.connected && integration.isLicenseActive && <EnabledBadge>LiveChat is connected</EnabledBadge>}
			<StatusBanner
				type="warning"
				isVisible={integration.connected && !integration.isLicenseActive}
				title="LiveChat license has expired"
			>
				To continue using the integration, <br />
				please{' '}
				<ColorResetLink href={LiveChatIntegrationUrls.SUBSCRIPTION} target="_blank" rel="noopener,noreferrer">
					renew it in the LiveChat app.
				</ColorResetLink>
			</StatusBanner>
			<VideoSection>
				<Video src="/videos/LCIntegration.mp4" />
			</VideoSection>
			<Explanation>
				LiveChat gives you and your customers more than just a chat. Use it to create a communication hub that connects
				multiple channels at once. And with our AI-based ChatBot and over 200 integrations, customers can stay in touch
				24/7.
			</Explanation>
			<FeaturesList>
				<FeatureItem>
					<ChatsIcon />
					Give customers the option to chat with you anytime
				</FeatureItem>
				<FeatureItem>
					<EyeIcon />
					Preview your website visitors in real time and invite them to chat
				</FeatureItem>
				<FeatureItem>
					<ClockIcon />
					Provide 24/7 support with AI-based ChatBot
				</FeatureItem>
			</FeaturesList>
			{integration.connected ? (
				<ButtonColumn>
					<BaseLink href={LiveChatIntegrationUrls.CHATS} target="_blank" rel="noopener">
						<StyledButton
							fullWidth
							kind="primary"
							type="button"
							onClick={() => track(EVENT_NAMES.LIVECHAT_GO_TO_CHATS_CLICKED)}
							disableHover
						>
							Go to chats
						</StyledButton>
					</BaseLink>
					<BaseLink href={LiveChatIntegrationUrls.SETTINGS} target="_blank" rel="noopener">
						<StyledButton
							fullWidth
							kind="secondary"
							type="button"
							onClick={() => track(EVENT_NAMES.LIVECHAT_GO_TO_SETTINGS_CLICKED)}
							disableHover
						>
							LiveChat Settings
						</StyledButton>
					</BaseLink>
					{mobile ? disconnectButton : <Portal context={ActionButtonsPortalContext}>{disconnectButton}</Portal>}
				</ButtonColumn>
			) : (
				<ButtonColumn>
					<UseAsyncFn key="connect" asyncFn={handleConnect}>
						{({ loading, callAsyncFn }) => (
							<StyledButton
								fullWidth
								kind="primary"
								type="button"
								loading={loading}
								onClick={() => {
									callAsyncFn()
									track(EVENT_NAMES.LIVECHAT_TRY_CLICKED)
								}}
								disableHover
							>
								{liveChatProductState === 'inactive' || liveChatProductState === 'unknown' ? 'Try' : 'Connect'} LiveChat
							</StyledButton>
						)}
					</UseAsyncFn>
					{mobile ? (
						<BaseLink href={LiveChatIntegrationUrls.PROMO_VIDEO} target="_blank" rel="noopener,noreferrer">
							<StyledButton
								fullWidth
								kind="secondary"
								type="button"
								onClick={() => track(EVENT_NAMES.LIVECHAT_LEARN_HOW_IT_WORKS_CLICKED)}
								disableHover
							>
								Learn how it works
							</StyledButton>
						</BaseLink>
					) : (
						<StyledButton
							fullWidth
							kind="secondary"
							type="button"
							onClick={() => {
								store.dispatch({ type: 'currentModal/set', payload: 'YouTubeVideoModal' })
								track(EVENT_NAMES.LIVECHAT_LEARN_HOW_IT_WORKS_CLICKED)
							}}
							disableHover
						>
							Learn how it works
						</StyledButton>
					)}
				</ButtonColumn>
			)}
			<DisconnectChatIntegrationModal
				name="LiveChat"
				caption="Your customers will still be able to reach out to you asynchronously via forms, but they won't be able to start a chat with you."
				handleDisconnect={handleDisconnect}
			/>
		</Wrapper>
	)
}

export default LiveChatIntegration
